import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  User,
  Calendar,
  GraduationCap,
  Heart,
  Users,
  Home
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../../services/api';

const StudentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  // const [searchParams] = useSearchParams();
  const carouselImages = [img1];


  const [studentData, setStudentData] = useState<any>(null);
  const [guardianData, setGuardianData] = useState<any>(null);
  const [addressData, setAddressData] = useState<any>(null);
  const [academicData, setAcademicData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'personal' | 'academic' | 'guardian' | 'address'>('personal');

  // Load student data
  const loadStudentData = useCallback(async () => {
    if (!studentId) return;

    setIsLoading(true);
    try {
      const [student, guardian, address, academic] = await Promise.all([
        attendanceApi.getStudentDetails(studentId),
        attendanceApi.getStudentGuardianDetails(studentId),
        attendanceApi.getStudentAddressProfile(studentId),
        attendanceApi.getStudentCurrentAcademicDetails(studentId)
      ]);

      setStudentData(student);
      setGuardianData(guardian);
      setAddressData(address);
      setAcademicData(academic);
    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadStudentData();
  }, [loadStudentData]);

  const getGenderLabel = (gender: string) => {
    return gender === '1' ? 'Male' : gender === '2' ? 'Female' : 'Other';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading student details...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <span className="text-gray-600">Student not found</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <User className="w-7 h-7" />
              {studentData.firstName} {studentData.middleName} {studentData.lastName}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span>Student ID: {studentData.studentID}</span>
              <span>Admission No: {studentData.admissionNo}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${studentData.gender === '1' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                }`}>
                {getGenderLabel(studentData.gender)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadStudentData}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <GraduationCap className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Class</p>
                <p className="text-2xl font-bold text-gray-900">{studentData.class} - {studentData.section}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Roll Number</p>
                <p className="text-2xl font-bold text-blue-600">{studentData.rollNo}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Session</p>
                <p className="text-2xl font-bold text-green-600">{studentData.academicSession}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100">
                <Heart className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="text-lg font-bold text-amber-600">
                  {new Date(studentData.dob).toLocaleDateString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('personal')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'personal'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal Info
                </div>
              </button>
              <button
                onClick={() => setActiveTab('academic')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'academic'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Academic Details
                </div>
              </button>
              <button
                onClick={() => setActiveTab('guardian')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'guardian'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Guardian Info
                </div>
              </button>
              <button
                onClick={() => setActiveTab('address')}
                className={`px-6 py-3 font-medium transition-colors ${activeTab === 'address'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Address
                </div>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Personal Info Tab */}
            {activeTab === 'personal' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="mt-1 text-base text-gray-900">
                      {studentData.firstName} {studentData.middleName} {studentData.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Gender</label>
                    <p className="mt-1 text-base text-gray-900">{getGenderLabel(studentData.gender)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                    <p className="mt-1 text-base text-gray-900">
                      {new Date(studentData.dob).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Blood Group</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.bloodGroup || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">APAAR Number</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.apaarNo || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">UBI ID</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.ubiID || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Admission Number</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.admissionNo}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Academic Tab */}
            {activeTab === 'academic' && academicData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Class</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.class}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Section</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.section}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Roll Number</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.rollNo}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Academic Session</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.academicSession}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Admission Category</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.admissionCategory}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Social Category</label>
                    <p className="mt-1 text-base text-gray-900">{studentData.socialCategory}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Guardian Tab */}
            {activeTab === 'guardian' && guardianData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Guardian Information</h3>
                  <p className="text-gray-600">Guardian details will be displayed here</p>
                </div>
              </div>
            )}

            {/* Address Tab */}
            {activeTab === 'address' && addressData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
                  <p className="text-gray-600">Address details will be displayed here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentDetail;
